import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSearchParams } from 'hooks/useSearchParams';
import { EQuoteStatus, IQuoteFilters } from 'types/quote.types';
import {
  buildQuoteFiltersSearchParams,
  IQuoteFiltersUrlState,
  parseQuoteFiltersFromSearchParams
} from '../utils/quoteFiltersUrl.utils';

interface IUseQuoteFiltersUrlOptions {
  status: EQuoteStatus;
  filters: IQuoteFilters;
  searchText: string;
  onUrlChange: (parsed: IQuoteFiltersUrlState) => void;
}

/**
 * Extrae solo los campos de filtros que se persisten en la URL.
 * @param filters - Filtros del listado
 */
const pickFiltersForUrl = (filters: IQuoteFilters) => ({
  countryIds: filters?.countryIds,
  organizationIds: filters?.organizationIds,
  userIds: filters?.userIds,
  quoteTypeIds: filters?.quoteTypeIds,
  slaRules: filters?.slaRules,
  sortData: filters?.sortData,
  sortValues: filters?.sortValues
});

/**
 * Sincroniza filtros de cotizaciones con los query params de la URL (sin paginación).
 */
export const useQuoteFiltersUrl = ({ status, filters, searchText, onUrlChange }: IUseQuoteFiltersUrlOptions) => {
  const location = useLocation();
  const [, setSearchParams] = useSearchParams();
  const lastSyncedSearchRef = useRef(location.search.replace(/^\?/, ''));

  const filtersForUrl = useMemo(() => pickFiltersForUrl(filters), [
    filters?.countryIds,
    filters?.organizationIds,
    filters?.userIds,
    filters?.quoteTypeIds,
    filters?.slaRules,
    filters?.sortData,
    filters?.sortValues
  ]);

  const nextSearchString = useMemo(
    () =>
      buildQuoteFiltersSearchParams({
        status,
        filters: filtersForUrl,
        searchText
      }).toString(),
    [status, searchText, filtersForUrl]
  );

  /**
   * Estado → URL: actualiza query params solo cuando el estado difiere de la URL.
   */
  useEffect(() => {
    const currentSearch = location.search.replace(/^\?/, '');

    const isExternalUrlChange =
      currentSearch !== lastSyncedSearchRef.current && currentSearch !== nextSearchString;
    if (isExternalUrlChange) return;

    if (nextSearchString === currentSearch) return;

    lastSyncedSearchRef.current = nextSearchString;
    setSearchParams(
      buildQuoteFiltersSearchParams({
        status,
        filters: filtersForUrl,
        searchText
      }),
      { replace: true }
    );
  }, [nextSearchString, location.search, status, searchText, filtersForUrl, setSearchParams]);

  /**
   * URL → Estado: rehidrata solo ante cambios externos (ej. botón atrás del navegador).
   */
  useEffect(() => {
    const currentSearch = location.search.replace(/^\?/, '');
    if (currentSearch === lastSyncedSearchRef.current) return;

    lastSyncedSearchRef.current = currentSearch;
    onUrlChange(parseQuoteFiltersFromSearchParams(new URLSearchParams(currentSearch)));
  }, [location.search, onUrlChange]);
};
